import { useRef, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useImpersonation, ImpersonationStartError } from '@/hooks/useImpersonation';
import { captureError } from '@/lib/sentry';
import {
  REASON_MIN,
  validateEmulationForm,
  type EmulationFormErrors,
} from './emulation-validation';

interface EmulateUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetUserId: string;
  targetEmail: string;
  targetName: string;
}

/**
 * Pre-flight modal for starting an emulation session. Captures:
 *   - Reason for access (required, ≥10 chars)
 *   - Admin password (re-verified server-side; required even in
 *     read-only mode)
 *   - Read-only toggle (default ON)
 *   - Suppress-notification toggle (default OFF)
 *
 * The Start button stays enabled while the form is invalid; on click we
 * validate, render inline field errors, focus the first invalid field, and
 * return before the RPC. Server-side errors (post-submit) are unchanged.
 *
 * Submitting calls useImpersonation().start() which handles the audit
 * insert, magiclink mint, session stash, and verifyOtp swap.
 */
export function EmulateUserModal({
  open,
  onOpenChange,
  targetUserId,
  targetEmail,
  targetName,
}: EmulateUserModalProps): JSX.Element {
  const { toast } = useToast();
  const { start, cancel, isStarting } = useImpersonation();

  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [readOnly, setReadOnly] = useState(true);
  const [suppress, setSuppress] = useState(false);
  const [suppressionReason, setSuppressionReason] = useState('');
  const [fieldErrors, setFieldErrors] = useState<EmulationFormErrors>({});
  const [error, setError] = useState<string | null>(null);
  // When start returns 409 open_session_exists, surface a recovery
  // affordance instead of just an error — the admin has an orphaned audit
  // row from a previous attempt where the session swap never landed.
  const [recoveryNeeded, setRecoveryNeeded] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);

  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const suppressionRef = useRef<HTMLTextAreaElement>(null);

  const reset = () => {
    setReason('');
    setPassword('');
    setReadOnly(true);
    setSuppress(false);
    setSuppressionReason('');
    setFieldErrors({});
    setError(null);
    setRecoveryNeeded(false);
    setIsRecovering(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  // Live clearing: drop a field's error the moment it becomes valid. We never
  // ADD an error on change — errors only appear on a Start click.
  const handleReasonChange = (value: string) => {
    setReason(value);
    setFieldErrors((prev) =>
      prev.reason && value.trim().length >= REASON_MIN
        ? { ...prev, reason: undefined }
        : prev,
    );
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    setFieldErrors((prev) =>
      prev.password && value.length > 0 ? { ...prev, password: undefined } : prev,
    );
  };

  const handleSuppressionReasonChange = (value: string) => {
    setSuppressionReason(value);
    setFieldErrors((prev) =>
      prev.suppressionReason && value.trim().length >= REASON_MIN
        ? { ...prev, suppressionReason: undefined }
        : prev,
    );
  };

  const handleSuppressChange = (next: boolean) => {
    setSuppress(next);
    // Turning suppression off unmounts its textarea — clear any stale error.
    if (!next) {
      setFieldErrors((prev) => ({ ...prev, suppressionReason: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isStarting) return;

    const errors = validateEmulationForm({
      reason,
      password,
      suppress,
      suppressionReason,
    });
    if (errors.reason || errors.password || errors.suppressionReason) {
      setFieldErrors(errors);
      // Focus the first invalid field in DOM order. The suppression textarea
      // is mounted iff `suppress` is true, which is the only state in which
      // its error can fire — so the ref is available whenever we focus it.
      if (errors.reason) {
        reasonRef.current?.focus();
      } else if (errors.password) {
        passwordRef.current?.focus();
      } else if (errors.suppressionReason) {
        suppressionRef.current?.focus();
      }
      return;
    }

    setFieldErrors({});
    setError(null);
    setRecoveryNeeded(false);
    try {
      await start({
        target_user_id: targetUserId,
        target_email: targetEmail,
        reason: reason.trim(),
        password,
        read_only: readOnly,
        suppress_notification: suppress,
        suppression_reason: suppress ? suppressionReason.trim() : undefined,
      });
      toast({
        title: 'Emulation started',
        description: `Now viewing the app as ${targetEmail}.`,
      });
      handleOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start emulation.';
      setError(msg);
      if (err instanceof ImpersonationStartError && err.code === 'open_session_exists') {
        setRecoveryNeeded(true);
      } else {
        captureError(err as Error, { feature: 'impersonation-start' });
      }
    }
  };

  const handleRecover = async () => {
    setIsRecovering(true);
    try {
      await cancel();
      setRecoveryNeeded(false);
      setError(null);
      toast({
        title: 'Previous attempt cleared',
        description: 'You can start a new emulation now.',
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to cancel previous attempt.';
      setError(msg);
      captureError(err as Error, { feature: 'impersonation-cancel' });
    } finally {
      setIsRecovering(false);
    }
  };

  const reasonChars = reason.trim().length;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* max-h + flex column + scrollable form region keeps the modal
          inside the viewport even when the suppress-notification toggle
          expands the form. Header + footer stay pinned; only the form
          area scrolls. */}
      <DialogContent className="max-w-md max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            Emulate user
          </DialogTitle>
          <DialogDescription>
            Sign in as <span className="font-mono">{targetEmail}</span> ({targetName}).
            All actions in this session are audit-logged and the user will be
            notified 24 hours after the session ends unless you suppress
            notification with a reason.
          </DialogDescription>
        </DialogHeader>

        {/* noValidate: native HTML5 constraint validation (required/minLength on
            the fields below) would otherwise swallow the submit event for empty
            fields, so handleSubmit's custom inline validation would never run.
            We run validateEmulationForm on submit instead. */}
        <form noValidate onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div className="space-y-1">
            <Label htmlFor="emulate-reason">Reason for access (required)</Label>
            <Textarea
              id="emulate-reason"
              ref={reasonRef}
              value={reason}
              onChange={(e) => handleReasonChange(e.target.value)}
              placeholder="e.g., Investigating ticket #1234 — user reports import is stuck"
              required
              minLength={REASON_MIN}
              rows={2}
              className="resize-none"
            />
            {fieldErrors.reason ? (
              <p className="text-xs text-destructive">{fieldErrors.reason}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Minimum {REASON_MIN} characters. Shown to the user in the audit-notification email.
                {reasonChars < REASON_MIN && (
                  <span className="ml-1 tabular-nums">
                    ({reasonChars} / {REASON_MIN})
                  </span>
                )}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="emulate-password">Confirm your password</Label>
            <PasswordInput
              id="emulate-password"
              ref={passwordRef}
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              autoComplete="current-password"
              required
            />
            {fieldErrors.password ? (
              <p className="text-xs text-destructive">{fieldErrors.password}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Re-verified server-side. Required every time.
              </p>
            )}
          </div>

          <div className="rounded-md border border-border p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm">
                <div className="font-medium">Read-only mode</div>
                <div className="text-xs text-muted-foreground">
                  Blocks mutations server-side. Safer default for debugging.
                </div>
              </div>
              <Switch checked={readOnly} onCheckedChange={setReadOnly} />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm">
                <div className="font-medium">Suppress user notification</div>
                <div className="text-xs text-muted-foreground">
                  Skips the 24h follow-up email. Use only for active security investigations.
                </div>
              </div>
              <Switch checked={suppress} onCheckedChange={handleSuppressChange} />
            </div>
            {suppress && (
              <div className="space-y-1 pt-2 border-t border-border">
                <Label htmlFor="suppression-reason" className="text-xs">
                  Suppression reason (required, ≥{REASON_MIN} chars)
                </Label>
                <Textarea
                  id="suppression-reason"
                  ref={suppressionRef}
                  value={suppressionReason}
                  onChange={(e) => handleSuppressionReasonChange(e.target.value)}
                  placeholder="e.g., Active fraud investigation, see Linear SEC-42"
                  rows={2}
                  className="resize-none"
                />
                {fieldErrors.suppressionReason && (
                  <p className="text-xs text-destructive">
                    {fieldErrors.suppressionReason}
                  </p>
                )}
              </div>
            )}
          </div>

            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive space-y-2">
                <div>{error}</div>
                {recoveryNeeded && (
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleRecover}
                      disabled={isRecovering}
                    >
                      {isRecovering ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                          Clearing…
                        </>
                      ) : (
                        'Cancel previous attempt'
                      )}
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      An earlier emulation didn't fully start. Clear it, then click Start emulation.
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="px-6 pb-6 pt-2 border-t bg-background">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={isStarting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isStarting} variant="destructive">
              {isStarting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Starting…
                </>
              ) : (
                'Start emulation'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
